import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import '@/app/styles.css';
import { localeReady } from '@/app/i18n';
import { installApiClient } from '@/app/api-client';
import { AppProviders } from '@/app/providers';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { router } from '@/app/router';
import { initSentry } from '@/shared/lib/sentry';
import { initInstallCapture } from '@/shared/pwa/install-prompt-store';

// Wire the generated client's auth bridge before any request can fire.
installApiClient();

// Capture `beforeinstallprompt` at startup — Chromium fires it once, early (on `/login`), long before
// the family shell's Install button mounts, so it MUST be caught here or it's lost (ADR-0018).
initInstallCapture();

// Error tracking (Sentry, M7 36a). Config-gated on VITE_SENTRY_DSN — a no-op (no init, no SDK
// loaded) when unset, which is the dev/test/CI default. Fire-and-forget so it never blocks paint;
// the SDK installs its global error/unhandledrejection handlers on init. PII is scrubbed in
// beforeSend (children's product).
void initSentry();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

// Wait (capped — see app/i18n.ts) for the persisted locale's catalogs so a returning `uk` user's
// first paint is already in Ukrainian, not an English flash that flips mid-view. The chunk fetch
// started at module import, in parallel with everything above; for `en` this resolves immediately.
void localeReady.then(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <AppProviders>
        {/* Last-resort boundary: any throw outside a route's errorElement (a lazy-chunk rejection on a
            route with none, or a render error in RouterProvider itself) degrades to the kind recovery
            card instead of white-screening the app. */}
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </AppProviders>
    </StrictMode>,
  );
});
